import * as React from 'react'
import { AlertTriangle, Factory, Loader2 } from 'lucide-react'
import {
  branchesApi,
  DEFAULT_FACTORYSIM_LOAD_URL,
  invalidateGraphData,
  invalidateTagInventoryQueryCache,
  loadFactorySimSession,
  useAppDispatch,
  useSwitchBranch,
  type LoadFactorySimResult
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { ConfirmDialog } from '../../ConfigurePage/components/DeleteConfirmDialog'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>
    if (typeof obj['message'] === 'string') return obj['message']
    if (typeof obj['error'] === 'string') return obj['error']
  }
  return 'Unknown error.'
}

function summarizeOpc(opc: LoadFactorySimResult['opc']): string {
  if (opc.skipped) return `OPC skipped (${opc.reason}).`
  return `Registered ${opc.tags.registered} FactorySim OPC tag${
    opc.tags.registered === 1 ? '' : 's'
  } at ${opc.endpoint}${opc.tags.failed > 0 ? ` (${opc.tags.failed} failed)` : ''}.`
}

export function LoadFactorySimCard({ embedded = false }: { embedded?: boolean }) {
  const dispatch = useAppDispatch()
  const { switchBranch } = useSwitchBranch()
  const [url, setUrl] = React.useState(DEFAULT_FACTORYSIM_LOAD_URL)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState<LoadFactorySimResult | null>(null)

  const runLoad = React.useCallback(async () => {
    setError('')
    setResult(null)
    setLoading(true)
    try {
      await switchBranch(undefined)
      const loaded = await loadFactorySimSession({ factorysimUrl: url.trim() })
      dispatch(branchesApi.util.invalidateTags(['Branches', 'Commits']))
      invalidateGraphData(dispatch)
      invalidateTagInventoryQueryCache()
      setResult(loaded)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
      setConfirmOpen(false)
    }
  }, [dispatch, switchBranch, url])

  const body = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold text-ink-2">Load FactorySim</h3>
        <p className="text-xs text-ink-3">
          Clear the current ontology and seed it from a running FactorySim session, including
          historian tag registration and FactorySim OPC subscriptions when OPC is up. Loopback URLs
          stay as <span className="font-mono">127.0.0.1</span> so a dockerized proxy can rewrite
          outbound to the host.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="factorysim-load-url" className="text-xs text-ink-3">
          FactorySim URL
        </Label>
        <Input
          id="factorysim-load-url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder={DEFAULT_FACTORYSIM_LOAD_URL}
          disabled={loading}
          autoComplete="off"
        />
      </div>

      <div
        role="alert"
        className="flex items-start gap-2 rounded-md border border-st-down/30 bg-st-down-bg px-3 py-2 text-xs text-st-down"
      >
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          This wipes Neo4j, Postgres, and historian Timescale the same way Clear databases does,
          then writes the live FactorySim line. There is no undo.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          className="border-st-down/40 bg-st-down-bg text-st-down hover:bg-st-down-bg hover:text-st-down"
          onClick={() => {
            setError('')
            setConfirmOpen(true)
          }}
          disabled={loading || url.trim() === ''}
        >
          {loading ? <Loader2 className="animate-spin" /> : <Factory />}
          {loading ? 'Loading…' : 'Load FactorySim'}
        </Button>
      </div>

      {error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2.5 py-1.5 text-xs text-st-down">
          {error}
        </div>
      ) : null}

      {result ? (
        <p className="text-xs text-ink-2">
          Loaded line <span className="font-mono">{result.lineId}</span> from{' '}
          <span className="font-mono">{result.persistedGatewayUrl}</span>: merged{' '}
          {result.nodesMerged} node{result.nodesMerged === 1 ? '' : 's'} and{' '}
          {result.relationshipsMerged} relationship
          {result.relationshipsMerged === 1 ? '' : 's'}. Registered{' '}
          {result.historianTags.registered} historian tag
          {result.historianTags.registered === 1 ? '' : 's'}. {summarizeOpc(result.opc)}
        </p>
      ) : null}
    </div>
  )

  const dialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Clear ontology and load FactorySim?"
      description={`This permanently deletes the main Neo4j graph, every branch, operational Postgres data, and this organization's historian rows when configured, then seeds ontology from the FactorySim session at ${url.trim() || DEFAULT_FACTORYSIM_LOAD_URL}. This cannot be undone.`}
      confirmLabel="Load FactorySim"
      destructive
      busy={loading}
      onConfirm={() => void runLoad()}
    />
  )

  if (embedded) {
    return (
      <>
        <div className="rounded-xl border border-st-down/30 bg-card px-4 py-4 shadow-sm">
          {body}
        </div>
        {dialog}
      </>
    )
  }

  return (
    <Card className="gap-3 border-st-down/30 py-4">
      <CardContent className="flex flex-col gap-3">{body}</CardContent>
      {dialog}
    </Card>
  )
}
